import { BUFFER_AREA, PARKING_SLOT_AREA_FACTOR, TRUCK_REQUIREMENT_FACTOR_1, TRUCK_REQUIREMENT_VALIDATION } from "../constants/global.constants";

/**
 * Generates a unique default name for a new building based on its type.
 *
 * @param {string} buildingTypeId - The unique identifier for the building type (e.g., "office", "production").
 * @param {string} typeName - The display name for the building type (e.g., "Office Building" or "Office Area").
 *                              The function removes any instance of "Area" or "Building" to form the prefix.
 * @param {Array} existingBuildings - An array of building objects. Each object should have a structure similar to:
 *                                    {
 *                                      data: {
 *                                        typeId: string,
 *                                        building: {
 *                                          name: string
 *                                        }
 *                                      }
 *                                    }
 * @returns {string} - The new, unique default building name, e.g., "Office 4".
 */
export function generateDefaultBuildingName(
  buildingTypeId,
  typeName,
  existingBuildings
) {
  // Derive the prefix by removing "Area" and "Building" from the type name.
  const prefix = typeName.replace("Area", "").replace("Building", "");

  // Use a regex that strictly matches names like "Office 1", "Office 2", etc.
  // The regex matches the entire string.
  const regex = new RegExp(`^${prefix}\\s*(\\d+)$`, "i");

  // Filter all buildings of the given type.
  const buildingsOfType = existingBuildings.filter(
    (b) => b.data.typeId === buildingTypeId
  );

  // Start with the candidate number equal to the count of buildings, since current building in list of existingBuildings.
  let candidateNumber = buildingsOfType.length;
  const existingNames = new Set(
    buildingsOfType.map((b) => (b.data.building.name || "").trim())
  );

  // Generate the candidate name.
  let candidateName = `${prefix} ${candidateNumber}`;

  // If a building with the candidate name already exists,
  // increment the candidate number until a unique name is found.
  while (existingNames.has(candidateName)) {
    candidateNumber++;
    candidateName = `${prefix} ${candidateNumber}`;
  }

  return candidateName;
}

/**
 * Calculates the total area required for a given number of docking stations (40t).
 *
 * The formula applied is:
 *
 *   Total Area = 38 * ( (1.5 * amount) + ((3.5 / 2) * amount) + 4.5 )
 *
 * @param {number} amount - The number of docking stations.
 * @returns {number} The calculated total area.
 */
export const calculateDockingStationsArea = (amount, truckRequirement) => {
  if (!amount || amount <= 0) {
    return 0;
  }
  const factor = TRUCK_REQUIREMENT_FACTOR_1[truckRequirement]
  return factor * (1.5 * amount + (3.5 / 2) * amount + 4.5);
};

/**
 * Calculates the total building area and the maximum inner building height
 * based on a list of nodes.
 *
 * @param {Array} nodes - Array of node objects.
 * @returns {Object} An object containing:
 *                   - buildingAreaSum: The total calculated building area.
 *                   - maxInnerBuildHeight: The highest calculated inner build height.
 */
export function calculateBuildingMetrics(nodes = []) {
  let buildingAreaSum = 0;
  let maxInnerBuildHeight = 0;

  nodes.forEach((n) => {
    // If the node has a `data` property with a `building` field, return that.
    // Otherwise, assume the node itself has the building properties.
    const building = n.data && n.data.building ? n.data.building : n;
    if (!building) return; // Skip if no building data exists

    // Calculate building area based on available ranges or parking slots.
    if (building.rangeMax && building.rangeMax > 0) {
      const areaWithBuffer = building.isolated ? (Math.sqrt(building.rangeMax) + 2 * BUFFER_AREA) ** 2 : building.rangeMax;
      buildingAreaSum += areaWithBuffer;
    } else if (building.rangeMin && building.rangeMin > 0) {
      const areaWithBuffer = building.isolated ? (Math.sqrt(building.rangeMin) + 2 * BUFFER_AREA) ** 2 : building.rangeMin;
      buildingAreaSum += areaWithBuffer;
    } else if (building.numParkingSlots && building.numParkingSlots > 0) {
      const areaWithBuffer = building.isolated ? (Math.sqrt(building.numParkingSlots * PARKING_SLOT_AREA_FACTOR) + 2 * BUFFER_AREA) ** 2 : building.numParkingSlots * PARKING_SLOT_AREA_FACTOR;
      buildingAreaSum += areaWithBuffer;
    }

    // Add area for docking stations if conditions are met.
    if (building.numDocks > 0 && building.truckRequirement !== -1) {
      buildingAreaSum += calculateDockingStationsArea(building.numDocks, building.truckRequirement);
    }

    // Determine the maximum inner build height.
    if (
      building.height &&
      building.height + 2 > maxInnerBuildHeight
    ) {
      maxInnerBuildHeight = building.height + 2;
    }
    if (
      building.stories &&
      building.stories * 3 + 2 > maxInnerBuildHeight
    ) {
      maxInnerBuildHeight = building.stories * 3 + 2;
    }
  });

  return { buildingAreaSum, maxInnerBuildHeight };
}
